import React, { useState, useCallback, useEffect } from 'react';
import { Button, Modal, Form, Input, Select, DatePicker, Popconfirm, Drawer, Timeline, Avatar, Spin, Empty } from 'antd';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import type { DropResult } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { PageHeader } from 'components/page-headers/page-headers';
import { CardToolbox, Main } from 'container/styled';
import FeatherIcon from 'feather-icons-react';
import { hasPermission } from 'utility/accessibility/hasPermission';
import openNotificationWithIcon from 'utility/notification';
import { TaskItemsApi } from 'api/clients/task-items-api';
import { UsersApi } from 'api/clients/users-api';
import { ProjectsApi } from 'api/clients/projects-api';
import type { TaskStatus } from 'api/models/task-status';
import type { TaskItemDetailDto } from 'api/models/task-item-detail-dto';
import type { TaskCommentDto } from 'api/models/task-comment-dto';
import type { TaskActivityDto } from 'api/models/task-activity-dto';
import dayjs from 'dayjs';
import styled from 'styled-components';

const taskItemsApi = new TaskItemsApi();
const usersApi = new UsersApi();
const projectsApi = new ProjectsApi();

const KanbanBoard = styled.div`
  display: flex;
  gap: 16px;
  padding: 20px;
  height: calc(100vh - 200px);
  overflow-x: auto;
`;

const KanbanColumn = styled.div<{ statusColor?: string }>`
  flex: 1;
  min-width: 300px;
  background: ${props => props.statusColor || '#f5f5f5'};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
`;

const ColumnHeader = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 16px;
  color: #262626;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TaskCount = styled.span`
  font-weight: 400;
  color: #8c8c8c;
  font-size: 13px;
`;

const TasksContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TaskCard = styled.div<{ isDragging: boolean }>`
  background: white;
  border-radius: 6px;
  padding: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s;
  opacity: ${props => props.isDragging ? 0.5 : 1};

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  }
`;

const TaskTitle = styled.div`
  font-weight: 500;
  font-size: 14px;
  color: #262626;
  margin-bottom: 8px;
`;

const TaskDescription = styled.div`
  font-size: 13px;
  color: #8c8c8c;
  margin-bottom: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const TaskMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const TaskMetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #8c8c8c;
`;

const PriorityBadge = styled.span<{ priority: number }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    switch (props.priority) {
      case 0: return '#f0f0f0'; // Low
      case 1: return '#e6f7ff'; // Medium
      case 2: return '#fff7e6'; // High
      case 3: return '#fff1f0'; // Urgent
      default: return '#f0f0f0';
    }
  }};
  color: ${props => {
    switch (props.priority) {
      case 0: return '#595959'; // Low
      case 1: return '#1890ff'; // Medium
      case 2: return '#fa8c16'; // High
      case 3: return '#ff4d4f'; // Urgent
      default: return '#595959';
    }
  }};
`;

const TaskActions = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
`;

const SimplifiedModal = styled(Modal)`
  .ant-modal-body {
    padding: 24px;
  }
`;

const InlineFieldGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 12px;
`;

const FieldIcon = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8c8c8c;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  gap: 16px;
`;

const EmptyStateIcon = styled.div`
  font-size: 64px;
  color: #d9d9d9;
  margin-bottom: 8px;
`;

const EmptyStateText = styled.div`
  font-size: 16px;
  color: #8c8c8c;
  margin-bottom: 8px;
`;

const DrawerSection = styled.div`
  margin-bottom: 24px;
`;

const DrawerSectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TaskDetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
`;

const TaskDetailItem = styled.div`
  .label {
    font-size: 12px;
    color: #8c8c8c;
    margin-bottom: 4px;
  }

  .value {
    font-size: 14px;
    color: #262626;
    font-weight: 500;
  }
`;

const CommentItem = styled.div`
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const CommentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const CommentAuthor = styled.div`
  font-weight: 500;
  font-size: 13px;
  color: #262626;
`;

const CommentDate = styled.div`
  font-size: 12px;
  color: #8c8c8c;
`;

const CommentContent = styled.div`
  font-size: 14px;
  color: #595959;
  line-height: 1.6;
  white-space: pre-wrap;
`;

const CommentForm = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
`;

const ActivityItem = styled.div`
  .activity-header {
    font-weight: 500;
    font-size: 13px;
    color: #262626;
    margin-bottom: 4px;
  }

  .activity-change {
    font-size: 12px;
    color: #595959;
    margin-bottom: 4px;
  }

  .activity-meta {
    font-size: 12px;
    color: #8c8c8c;
  }
`;

export const TasksPage: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [tasks, setTasks] = useState<any[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTask, setEditingTask] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [detailsDrawerVisible, setDetailsDrawerVisible] = useState(false);
  const [selectedTaskDetails, setSelectedTaskDetails] = useState<TaskItemDetailDto | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  const statuses = [
    { value: 0, label: t('tasks:status.open', 'Open'), key: 'open', color: '#f5f5f5' },
    { value: 1, label: t('tasks:status.inProgress', 'In Progress'), key: 'inProgress', color: '#e6f7ff' },
    { value: 2, label: t('tasks:status.completed', 'Completed'), key: 'completed', color: '#f6ffed' },
    { value: 3, label: t('tasks:status.cancelled', 'Cancelled'), key: 'cancelled', color: '#fff1f0' },
  ];

  const fetchTasks = useCallback(async () => {
    try {
      const response = await taskItemsApi.apiTaskItemsGet({
        pageNumber: 1,
        pageSize: 9999, // Fetch all tasks for Kanban view
      });
      setTasks(response.data.items || []);
    } catch {
      openNotificationWithIcon('error', t('tasks:errors.fetchFailed', 'Failed to fetch tasks'));
    }
  }, [t]);

  const fetchUsers = useCallback(async () => {
    try {
      // NOTE: Currently fetching all ACTIVE users. The API does not support
      // filtering users by specific permissions (e.g., users with 'tasks:assign' permission).
      // To implement permission-based filtering, backend API changes would be required.
      const response = await usersApi.apiUsersListGet({
        pageNumber: 1,
        pageSize: 9999,
        filterType: 1, // ACTIVE users only
      });
      setUsers(response.data.items || []);
    } catch (err) {
      console.error('Failed to fetch users', err);
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await projectsApi.apiProjectsGet({
        pageNumber: 1,
        pageSize: 9999,
        isActive: true,
      });
      setProjects(response.data.items || []);
    } catch (err) {
      console.error('Failed to fetch projects', err);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchUsers();
    fetchProjects();
  }, [fetchTasks, fetchUsers, fetchProjects]);

  const handleCreate = () => {
    setEditingTask(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (task: any) => {
    setEditingTask(task);
    form.setFieldsValue({
      ...task,
      dueDate: task.dueDate ? dayjs(task.dueDate) : null,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await taskItemsApi.apiTaskItemsIdDelete({ id });
      openNotificationWithIcon('success', t('tasks:success.deleted', 'Task deleted successfully'));
      fetchTasks();
    } catch {
      openNotificationWithIcon('error', t('tasks:errors.deleteFailed', 'Failed to delete task'));
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const payload = {
        ...values,
        dueDate: values.dueDate ? values.dueDate.toISOString() : null,
      };

      if (editingTask) {
        await taskItemsApi.apiTaskItemsIdPut({
          id: editingTask.id,
          updateTaskItemCommand: { ...payload, id: editingTask.id },
        });
        openNotificationWithIcon('success', t('tasks:success.updated', 'Task updated successfully'));
      } else {
        await taskItemsApi.apiTaskItemsPost({
          createTaskItemCommand: payload,
        });
        openNotificationWithIcon('success', t('tasks:success.created', 'Task created successfully'));
      }

      setModalVisible(false);
      fetchTasks();
    } catch {
      openNotificationWithIcon('error', t('tasks:errors.saveFailed', 'Failed to save task'));
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const newStatus = parseInt(destination.droppableId, 10) as TaskStatus;
    const taskId = parseInt(draggableId, 10);

    // Optimistic update
    const updatedTasks = tasks.map(task =>
      task.id === taskId ? { ...task, status: newStatus } : task
    );
    setTasks(updatedTasks);

    // API call
    try {
      const task = tasks.find(t => t.id === taskId);
      await taskItemsApi.apiTaskItemsIdPut({
        id: taskId,
        updateTaskItemCommand: {
          id: taskId,
          title: task.title,
          description: task.description,
          priority: task.priority,
          status: newStatus,
          assignedToId: task.assignedToId,
          projectId: task.projectId,
          dueDate: task.dueDate,
        },
      });
      openNotificationWithIcon('success', t('tasks:success.statusUpdated', 'Task status updated'));
    } catch {
      openNotificationWithIcon('error', t('tasks:errors.statusUpdateFailed', 'Failed to update task status'));
      // Revert on error
      fetchTasks();
    }
  };

  const getPriorityLabel = (priority: number) => {
    const labels = [
      t('tasks:priority.low', 'Low'),
      t('tasks:priority.medium', 'Medium'),
      t('tasks:priority.high', 'High'),
      t('tasks:priority.urgent', 'Urgent')
    ];
    return labels[priority] || labels[0];
  };

  const getTasksByStatus = (status: number) => {
    return tasks.filter(task => task.status === status);
  };

  const handleTaskCardClick = async (taskId: number) => {
    if (!hasPermission('tasks:view')) {
      openNotificationWithIcon('error', t('tasks:errors.noPermission', 'You do not have permission to view task details'));
      return;
    }

    setDetailsDrawerVisible(true);
    setLoadingDetails(true);
    setSelectedTaskDetails(null);

    try {
      const response = await taskItemsApi.apiTaskItemsIdGet({ id: taskId });
      setSelectedTaskDetails(response.data);
    } catch {
      openNotificationWithIcon('error', t('tasks:errors.fetchDetailsFailed', 'Failed to fetch task details'));
      setDetailsDrawerVisible(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseDetailsDrawer = () => {
    setDetailsDrawerVisible(false);
    setSelectedTaskDetails(null);
    setNewComment('');
  };

  const handleAddComment = async () => {
    if (!selectedTaskDetails?.id || !newComment.trim()) {
      return;
    }

    if (!hasPermission('tasks:add-comment')) {
      openNotificationWithIcon('error', t('tasks:errors.noPermissionComment', 'You do not have permission to add comments'));
      return;
    }

    setSubmittingComment(true);
    try {
      await taskItemsApi.apiTaskItemsIdCommentsPost({
        id: selectedTaskDetails.id,
        addTaskCommentCommand: {
          taskItemId: selectedTaskDetails.id,
          content: newComment,
        },
      });

      openNotificationWithIcon('success', t('tasks:success.commentAdded', 'Comment added successfully'));
      setNewComment('');

      // Refresh task details
      const response = await taskItemsApi.apiTaskItemsIdGet({ id: selectedTaskDetails.id });
      setSelectedTaskDetails(response.data);
    } catch {
      openNotificationWithIcon('error', t('tasks:errors.addCommentFailed', 'Failed to add comment'));
    } finally {
      setSubmittingComment(false);
    }
  };

  const getActivityTypeLabel = (activityType: number) => {
    const labels = [
      t('tasks:activityType.created', 'Created'),
      t('tasks:activityType.statusChanged', 'Status Changed'),
      t('tasks:activityType.priorityChanged', 'Priority Changed'),
      t('tasks:activityType.assigneeChanged', 'Assignee Changed'),
      t('tasks:activityType.dueDateChanged', 'Due Date Changed'),
      t('tasks:activityType.updated', 'Updated'),
      t('tasks:activityType.completed', 'Completed'),
    ];
    return labels[activityType] || t('tasks:activityType.unknown', 'Unknown');
  };

  const getActivityIcon = (activityType: number) => {
    const icons = ['plus-circle', 'arrow-right', 'flag', 'user', 'calendar', 'edit', 'check-circle'];
    return icons[activityType] || 'activity';
  };

  const getActivityColor = (activityType: number) => {
    const colors = ['#52c41a', '#1890ff', '#fa8c16', '#722ed1', '#13c2c2', '#8c8c8c', '#52c41a'];
    return colors[activityType] || '#8c8c8c';
  };

  return (
    <>
      <CardToolbox>
        <PageHeader
          title={t('tasks:title', 'Tasks')}
          buttons={[
            hasPermission('tasks:create') && (
              <Button type="primary" key="create" onClick={handleCreate}>
                <FeatherIcon icon="plus" size={14} /> {t('tasks:actions.createTask', 'Create Task')}
              </Button>
            ),
          ].filter(Boolean)}
        />
      </CardToolbox>

      <Main>
        {tasks.length === 0 ? (
          <EmptyState>
            <EmptyStateIcon>
              <FeatherIcon icon="check-square" size={64} />
            </EmptyStateIcon>
            <EmptyStateText>{t('tasks:emptyState.noTasks', 'No tasks yet')}</EmptyStateText>
            {hasPermission('tasks:create') && (
              <Button type="primary" size="large" onClick={handleCreate}>
                <FeatherIcon icon="plus" size={16} /> {t('tasks:emptyState.createFirstTask', 'Create Your First Task')}
              </Button>
            )}
          </EmptyState>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <KanbanBoard>
              {statuses.map(status => {
                const statusTasks = getTasksByStatus(status.value);
                return (
                  <KanbanColumn key={status.value} statusColor={status.color}>
                  <ColumnHeader>
                    <span>{status.label}</span>
                    <TaskCount>{statusTasks.length}</TaskCount>
                  </ColumnHeader>
                  <Droppable droppableId={status.value.toString()}>
                    {(provided) => (
                      <TasksContainer
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {statusTasks.map((task, index) => (
                          <Draggable
                            key={task.id}
                            draggableId={task.id.toString()}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <TaskCard
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                isDragging={snapshot.isDragging}
                                onClick={() => handleTaskCardClick(task.id)}
                              >
                                <TaskTitle>{task.title}</TaskTitle>
                                {task.description && (
                                  <TaskDescription>{task.description}</TaskDescription>
                                )}
                                <TaskMeta>
                                  <PriorityBadge priority={task.priority}>
                                    {getPriorityLabel(task.priority)}
                                  </PriorityBadge>
                                  {task.assignedToName && (
                                    <TaskMetaItem>
                                      <FeatherIcon icon="user" size={12} />
                                      {task.assignedToName}
                                    </TaskMetaItem>
                                  )}
                                  {task.dueDate && (
                                    <TaskMetaItem>
                                      <FeatherIcon icon="calendar" size={12} />
                                      {dayjs(task.dueDate).format('MMM DD')}
                                    </TaskMetaItem>
                                  )}
                                  {task.projectName && (
                                    <TaskMetaItem>
                                      <FeatherIcon icon="folder" size={12} />
                                      {task.projectName}
                                    </TaskMetaItem>
                                  )}
                                </TaskMeta>
                                {hasPermission('tasks:delete') && (
                                  <TaskActions onClick={(e) => e.stopPropagation()}>
                                    <Popconfirm
                                      title={t('tasks:confirmations.deleteTask', 'Delete task?')}
                                      onConfirm={(e) => {
                                        e?.stopPropagation();
                                        handleDelete(task.id);
                                      }}
                                    >
                                      <Button
                                        type="text"
                                        size="small"
                                        danger
                                        icon={<FeatherIcon icon="trash-2" size={14} />}
                                      />
                                    </Popconfirm>
                                  </TaskActions>
                                )}
                              </TaskCard>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </TasksContainer>
                    )}
                  </Droppable>
                </KanbanColumn>
              );
              })}
            </KanbanBoard>
          </DragDropContext>
        )}
      </Main>

      <SimplifiedModal
        title={editingTask ? t('tasks:modal.editTitle', 'Edit Task') : t('tasks:modal.createTitle', 'Create Task')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={500}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="title" rules={[{ required: true, message: t('tasks:validation.titleRequired', 'Title is required') }]}>
            <Input placeholder={t('tasks:fields.title', 'Title')} size="large" bordered={false} style={{ fontSize: 16 }} />
          </Form.Item>

          <Form.Item name="description">
            <Input.TextArea
              placeholder={t('tasks:fields.description', 'Description')}
              rows={4}
              bordered={false}
              style={{ fontSize: 14 }}
            />
          </Form.Item>

          <InlineFieldGroup>
            <FieldIcon>
              <FeatherIcon icon="circle" size={16} />
            </FieldIcon>
            <Form.Item name="status" initialValue={0} style={{ marginBottom: 0, flex: 1 }}>
              <Select size="large" bordered={false}>
                <Select.Option value={0}>{t('tasks:status.open', 'Open')}</Select.Option>
                <Select.Option value={1}>{t('tasks:status.inProgress', 'In Progress')}</Select.Option>
                <Select.Option value={2}>{t('tasks:status.completed', 'Completed')}</Select.Option>
                <Select.Option value={3}>{t('tasks:status.cancelled', 'Cancelled')}</Select.Option>
              </Select>
            </Form.Item>
          </InlineFieldGroup>

          <InlineFieldGroup>
            <FieldIcon>
              <FeatherIcon icon="user" size={16} />
            </FieldIcon>
            <Form.Item name="assignedToId" style={{ marginBottom: 0, flex: 1 }}>
              <Select
                allowClear
                showSearch
                placeholder={t('tasks:fields.assignTo', 'Assign to...')}
                optionFilterProp="children"
                filterOption={(input: string, option: any) => {
                  const children = option?.children;
                  if (typeof children === 'string') {
                    return children.toLowerCase().includes(input.toLowerCase());
                  }
                  return false;
                }}
                size="large"
                bordered={false}
              >
                {users.map((user: any) => (
                  <Select.Option key={user.id} value={user.id}>
                    {`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </InlineFieldGroup>

          <InlineFieldGroup>
            <FieldIcon>
              <FeatherIcon icon="calendar" size={16} />
            </FieldIcon>
            <Form.Item name="dueDate" style={{ marginBottom: 0, flex: 1 }}>
              <DatePicker
                placeholder={t('tasks:fields.dueDate', 'Due date')}
                style={{ width: '100%' }}
                size="large"
                bordered={false}
              />
            </Form.Item>
          </InlineFieldGroup>

          <InlineFieldGroup>
            <FieldIcon>
              <FeatherIcon icon="flag" size={16} />
            </FieldIcon>
            <Form.Item name="priority" initialValue={0} style={{ marginBottom: 0, flex: 1 }}>
              <Select size="large" bordered={false}>
                <Select.Option value={0}>{t('tasks:priority.low', 'Low')}</Select.Option>
                <Select.Option value={1}>{t('tasks:priority.medium', 'Medium')}</Select.Option>
                <Select.Option value={2}>{t('tasks:priority.high', 'High')}</Select.Option>
                <Select.Option value={3}>{t('tasks:priority.urgent', 'Urgent')}</Select.Option>
              </Select>
            </Form.Item>
          </InlineFieldGroup>

          <InlineFieldGroup>
            <FieldIcon>
              <FeatherIcon icon="folder" size={16} />
            </FieldIcon>
            <Form.Item name="projectId" style={{ marginBottom: 0, flex: 1 }}>
              <Select
                allowClear
                showSearch
                placeholder={t('tasks:fields.selectProject', 'Select project...')}
                optionFilterProp="children"
                filterOption={(input: string, option: any) => {
                  const children = option?.children;
                  if (typeof children === 'string') {
                    return children.toLowerCase().includes(input.toLowerCase());
                  }
                  return false;
                }}
                size="large"
                bordered={false}
              >
                {projects.map((project: any) => (
                  <Select.Option key={project.id} value={project.id}>
                    {project.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </InlineFieldGroup>
        </Form>
      </SimplifiedModal>

      <Drawer
        title={t('tasks:details.title', 'Task Details')}
        placement="right"
        onClose={handleCloseDetailsDrawer}
        open={detailsDrawerVisible}
        width={600}
        extra={
          hasPermission('tasks:update') && selectedTaskDetails && (
            <Button
              type="default"
              icon={<FeatherIcon icon="edit" size={14} />}
              onClick={() => {
                handleEdit(tasks.find(t => t.id === selectedTaskDetails.id));
                setDetailsDrawerVisible(false);
              }}
            >
              {t('tasks:actions.edit', 'Edit')}
            </Button>
          )
        }
      >
        {loadingDetails ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
          </div>
        ) : selectedTaskDetails ? (
          <>
            <DrawerSection>
              <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '16px' }}>
                {selectedTaskDetails.title}
              </h2>
              {selectedTaskDetails.description && (
                <p style={{ fontSize: '14px', color: '#595959', marginBottom: '16px', lineHeight: 1.6 }}>
                  {selectedTaskDetails.description}
                </p>
              )}

              <TaskDetailGrid>
                <TaskDetailItem>
                  <div className="label">{t('tasks:fields.status', 'Status')}</div>
                  <div className="value">
                    {statuses.find(s => s.value === selectedTaskDetails.status)?.label || '-'}
                  </div>
                </TaskDetailItem>

                <TaskDetailItem>
                  <div className="label">{t('tasks:fields.priority', 'Priority')}</div>
                  <div className="value">
                    <PriorityBadge priority={selectedTaskDetails.priority || 0}>
                      {getPriorityLabel(selectedTaskDetails.priority || 0)}
                    </PriorityBadge>
                  </div>
                </TaskDetailItem>

                <TaskDetailItem>
                  <div className="label">{t('tasks:fields.assignedTo', 'Assigned To')}</div>
                  <div className="value">{selectedTaskDetails.assignedToName || '-'}</div>
                </TaskDetailItem>

                <TaskDetailItem>
                  <div className="label">{t('tasks:fields.project', 'Project')}</div>
                  <div className="value">{selectedTaskDetails.projectName || '-'}</div>
                </TaskDetailItem>

                <TaskDetailItem>
                  <div className="label">{t('tasks:fields.dueDate', 'Due Date')}</div>
                  <div className="value">
                    {selectedTaskDetails.dueDate ? dayjs(selectedTaskDetails.dueDate).format('MMM DD, YYYY') : '-'}
                  </div>
                </TaskDetailItem>

                <TaskDetailItem>
                  <div className="label">{t('tasks:fields.createdBy', 'Created By')}</div>
                  <div className="value">{selectedTaskDetails.createdByName || '-'}</div>
                </TaskDetailItem>
              </TaskDetailGrid>
            </DrawerSection>

            <DrawerSection>
              <DrawerSectionTitle>
                <FeatherIcon icon="message-circle" size={16} />
                {t('tasks:details.comments', 'Comments')} ({selectedTaskDetails.comments?.length || 0})
              </DrawerSectionTitle>

              {selectedTaskDetails.comments && selectedTaskDetails.comments.length > 0 ? (
                selectedTaskDetails.comments.map((comment: TaskCommentDto) => (
                  <CommentItem key={comment.id}>
                    <CommentHeader>
                      <Avatar size="small" icon={<FeatherIcon icon="user" size={12} />} />
                      <div>
                        <CommentAuthor>{comment.createdByName || t('tasks:details.unknown', 'Unknown')}</CommentAuthor>
                        <CommentDate>{dayjs(comment.createdAt).format('MMM DD, YYYY [at] HH:mm')}</CommentDate>
                      </div>
                    </CommentHeader>
                    <CommentContent>{comment.content}</CommentContent>
                  </CommentItem>
                ))
              ) : (
                <Empty
                  image={<FeatherIcon icon="message-circle" size={32} />}
                  description={t('tasks:details.noComments', 'No comments yet')}
                  style={{ marginBottom: '16px' }}
                />
              )}

              {hasPermission('tasks:add-comment') && (
                <CommentForm>
                  <Input.TextArea
                    rows={3}
                    placeholder={t('tasks:details.addCommentPlaceholder', 'Add a comment...')}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    style={{ marginBottom: '8px' }}
                  />
                  <Button
                    type="primary"
                    icon={<FeatherIcon icon="send" size={14} />}
                    onClick={handleAddComment}
                    loading={submittingComment}
                    disabled={!newComment.trim()}
                  >
                    {t('tasks:details.submitComment', 'Add Comment')}
                  </Button>
                </CommentForm>
              )}
            </DrawerSection>

            <DrawerSection>
              <DrawerSectionTitle>
                <FeatherIcon icon="activity" size={16} />
                {t('tasks:details.activity', 'Activity Log')}
              </DrawerSectionTitle>

              {selectedTaskDetails.activities && selectedTaskDetails.activities.length > 0 ? (
                <Timeline style={{ marginTop: '16px' }}>
                  {selectedTaskDetails.activities.map((activity: TaskActivityDto) => (
                    <Timeline.Item
                      key={activity.id}
                      dot={
                        <FeatherIcon
                          icon={getActivityIcon(activity.activityType || 0)}
                          size={14}
                          color={getActivityColor(activity.activityType || 0)}
                        />
                      }
                    >
                      <ActivityItem>
                        <div className="activity-header">
                          {getActivityTypeLabel(activity.activityType || 0)}
                        </div>
                        {activity.oldValue && activity.newValue && (
                          <div className="activity-change">
                            <span style={{ color: '#ff4d4f', textDecoration: 'line-through' }}>
                              {activity.oldValue}
                            </span>
                            {' → '}
                            <span style={{ color: '#52c41a', fontWeight: 500 }}>
                              {activity.newValue}
                            </span>
                          </div>
                        )}
                        <div className="activity-meta">
                          {activity.createdByName || t('tasks:details.unknown', 'Unknown')} •{' '}
                          {dayjs(activity.createdAt).format('MMM DD, YYYY [at] HH:mm')}
                        </div>
                      </ActivityItem>
                    </Timeline.Item>
                  ))}
                </Timeline>
              ) : (
                <Empty
                  image={<FeatherIcon icon="activity" size={32} />}
                  description={t('tasks:details.noActivity', 'No activity yet')}
                  style={{ marginTop: '16px' }}
                />
              )}
            </DrawerSection>
          </>
        ) : null}
      </Drawer>
    </>
  );
};
